import { toast } from 'sonner';
import { Check, Copy } from 'lucide-react';
import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';

/**
 * Copies text to the clipboard, with the confirmation shown on the button
 * itself rather than only as a toast.
 *
 * The inline tick matters more than it looks: clinicians copy a transcript to
 * paste it into another system, and a copy that silently failed is only
 * discovered after pasting the wrong thing. The button state says what
 * happened at the point of action.
 */
interface Props {
    /** What the toast calls this, e.g. "AI transcript". */
    describedAs?: string;
    label?: string;
    /** Resolved lazily so a long transcript is only serialised on click. */
    text: string | (() => string);
}

export function CopyButton({ text, describedAs, label = 'Copy' }: Props) {
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (!copied) {
            return;
        }

        const timer = setTimeout(() => setCopied(false), 2000);

        return () => clearTimeout(timer);
    }, [copied]);

    async function copy() {
        const value = typeof text === 'function' ? text() : text;

        if (value.trim() === '') {
            toast.error('Nothing to copy yet.');

            return;
        }

        try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            toast.success(
                describedAs ? `${describedAs} copied` : 'Copied to clipboard',
            );
        } catch {
            // `navigator.clipboard` needs a secure context and permission, and
            // is simply absent in some embedded browsers. Saying so beats a
            // button that appears to work and quietly does nothing.
            toast.error('Could not copy — your browser blocked clipboard access.');
        }
    }

    return (
        <Button
            size="sm"
            type="button"
            variant="outline"
            className="gap-1.5"
            onClick={copy}
        >
            {copied ? (
                <Check className="h-3.5 w-3.5" />
            ) : (
                <Copy className="h-3.5 w-3.5" />
            )}
            {copied ? 'Copied' : label}
        </Button>
    );
}
